import React, { forwardRef, useContext } from 'react';
import { useController } from 'react-hook-form';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { ComboboxProps } from '../Combobox';
import { ComboboxField } from '../Combobox/ComboboxField';
import { FormContext } from './Form';
import { BoundComponentProps } from './types';

export type BoundComboboxProps = ComboboxProps & BoundComponentProps;

export const BoundCombobox = forwardRef<HTMLButtonElement, BoundComboboxProps>(
  (
    {
      id,
      control,
      name,
      defaultValue,
      rules,
      label,
      required,
      readOnly,
      options,
      disabled,
      onChange: outerOnChange,
      ...rest
    },
    ref,
  ) => {
    const { control: contextControl } = useContext(FormContext);

    const {
      field: { ref: internalRef, value, onChange, ...selectProps },
      fieldState: { error },
    } = useController({
      name,
      control: control ?? contextControl,
      defaultValue,
      rules: {
        required: required && 'This field is required',
        ...rules,
      },
    });

    const combinedRef = useMergeRefs(ref, internalRef);

    return (
      <ComboboxField
        ref={combinedRef as any}
        id={id}
        label={label}
        value={value}
        options={options}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        error={error?.message}
        onChange={(keys: (string | number)[], changed) => {
          onChange(keys);
          outerOnChange?.(keys, changed);
        }}
        {...selectProps}
        {...rest}
      />
    );
  },
);

BoundCombobox.displayName = 'BoundCombobox';
